/**
 * The page of one hypothesis, filled from the API (ADR-0095). `/cases/:id/hypotheses/:id` was
 * the research detail before: an id no hypothesis answers to (404) is taken as a research id
 * and opens the research detail, so old links keep working. With the case's *Spis k dátumu* set,
 * the hypothesis is as it stood on that day, under its banner, and takes no writes (R-D4).
 */
import type { UseQueryResult } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Navigate } from 'react-router-dom'
import { toast } from 'sonner'
import { AddAssessmentForm } from '@/components/analytic-hypotheses/AddAssessmentForm'
import {
  HypothesisDetailFailed,
  HypothesisDetailLoading,
  HypothesisDetailView,
  type HypothesisLinks,
} from '@/components/analytic-hypotheses/HypothesisDetailView'
import { useCaseAsOf } from '@/features/case-as-of/CaseAsOf'
import { CaseAsOfBanner } from '@/features/case-as-of/CaseAsOfBanner'
import type {
  AnalyticHypothesisDetail,
  HypothesisAssessmentCreate,
  HypothesisResearchRun,
  HypothesisResearchRunsResponse,
} from '@/types/analytic-hypothesis'
import { useHypothesisResearchRuns } from './evidence-queries'
import { HypothesisEvidencePanel, HypothesisResearchPanel } from './HypothesisEvidence'
import { investigationPath, researchPath } from './paths'
import { isNotFound, messageOf, useAnalyticHypothesis, useHypothesisWrites } from './queries'
import { useEvidenceSources } from './useEvidenceSources'

function linksFor(caseId: string, runs: readonly HypothesisResearchRun[]): HypothesisLinks {
  return {
    entity: nodeId => `/cases/${caseId}/explore-graph?focus=${encodeURIComponent(nodeId)}`,
    evidence: ref => {
      if (ref.kind === 'investigation') return investigationPath(caseId, ref.id)
      const run = ref.kind === 'research_run' ? runs.find(item => item.id === ref.id) : undefined
      return run ? researchPath(caseId, run.research_id) : null
    },
  }
}

/** The runs as the assessment form cites them, stable while the read is. */
function useCitableRuns(runs: UseQueryResult<HypothesisResearchRunsResponse, unknown>) {
  return useMemo(
    () => ({ data: runs.data?.runs, isError: runs.isError }),
    [runs.data, runs.isError]
  )
}

function Loaded({ caseId, hypothesis }: { caseId: string; hypothesis: AnalyticHypothesisDetail }) {
  const { day, asOf } = useCaseAsOf()
  const writes = useHypothesisWrites(caseId)
  const runs = useHypothesisResearchRuns(caseId, hypothesis.id, asOf)
  const citableRuns = useCitableRuns(runs)
  const sources = useEvidenceSources(caseId, citableRuns)
  // The file as of a past day is read-only: what is written now would not show in it.
  const canWrite = hypothesis.member_role !== 'viewer' && !day
  const open = hypothesis.status === 'open'
  const panel = { caseId, hypothesisId: hypothesis.id, canWrite, open }

  const toggleStatus = async () => {
    const outcome = await writes.setClosed(hypothesis.id, open)
    if (outcome.message) toast.error(outcome.message)
  }
  const assess = async (body: HypothesisAssessmentCreate) =>
    messageOf(await writes.addAssessment(hypothesis.id, body))

  return (
    <>
      <CaseAsOfBanner className="mb-4" />
      <HypothesisDetailView
        hypothesis={hypothesis}
        links={linksFor(caseId, citableRuns.data ?? [])}
        statusAction={canWrite ? { onToggle: toggleStatus, pending: writes.statusPending } : null}
        form={canWrite && open ? <AddAssessmentForm sources={sources} onSubmit={assess} /> : null}
      >
        <HypothesisEvidencePanel {...panel} />
        <HypothesisResearchPanel {...panel} runs={runs} />
      </HypothesisDetailView>
    </>
  )
}

export function HypothesisDetail({
  caseId,
  hypothesisId,
}: {
  caseId: string
  hypothesisId: string
}) {
  const { asOf } = useCaseAsOf()
  const query = useAnalyticHypothesis(caseId, hypothesisId, asOf)
  if (query.data) return <Loaded caseId={caseId} hypothesis={query.data} />
  if (query.isError && isNotFound(query.error)) {
    return <Navigate to={researchPath(caseId, hypothesisId)} replace />
  }
  if (query.isError) return <HypothesisDetailFailed onRetry={() => void query.refetch()} />
  return <HypothesisDetailLoading />
}
